// Settings — slide-in top-right drawer; mirrors left/right drawer geometry.

import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { atmosWS } from "../lib/atmosWS";
import { atmosStore } from "../lib/atmosStore";

type ThemeChoice = "light" | "dark" | "system";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

const THEME_KEY = "atmos.theme";

function detectTheme(): ThemeChoice {
  const saved = localStorage.getItem(THEME_KEY) as ThemeChoice | null;
  if (saved === "light" || saved === "dark" || saved === "system") return saved;
  return document.body.classList.contains("theme-dark") ? "dark" : "light";
}

function applyTheme(choice: ThemeChoice): void {
  let dark = choice === "dark";
  if (choice === "system") {
    dark = window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  }
  document.body.classList.toggle("theme-dark", dark);
  try {
    localStorage.setItem(THEME_KEY, choice);
  } catch {
    // localStorage can throw in private mode; non-fatal.
  }
}

function buildThemeRow(initial: ThemeChoice): HTMLElement {
  const row = el("div", "settings__row settings__row--theme");
  const left = el("div", "settings__row-label");
  left.appendChild(el("span", undefined, "Theme"));
  row.appendChild(left);

  const seg = el("div", "settings__segmented");
  seg.setAttribute("role", "tablist");
  const opts: [ThemeChoice, string][] = [
    ["light", "Light"], ["dark", "Dark"], ["system", "System"],
  ];
  const btns = new Map<ThemeChoice, HTMLButtonElement>();
  function setActive(next: ThemeChoice): void {
    for (const [k, b] of btns) {
      const on = k === next;
      b.classList.toggle("settings__chip--active", on);
      b.setAttribute("aria-selected", on ? "true" : "false");
    }
    applyTheme(next);
  }
  for (const [k, label] of opts) {
    const chip = el("button", "settings__chip", label);
    chip.type = "button";
    chip.setAttribute("role", "tab");
    chip.addEventListener("click", () => setActive(k));
    btns.set(k, chip);
    seg.appendChild(chip);
  }
  setActive(initial);
  row.appendChild(seg);
  return row;
}

function buildStaticRow(
  iconKey: Parameters<typeof Icon>[0] | null,
  label: string,
  value: string,
  opts: { destructive?: boolean; chevron?: boolean } = {},
): HTMLElement {
  const row = el("button", "settings__row settings__row--tap");
  row.type = "button";
  if (opts.destructive) row.classList.add("settings__row--destructive");
  const left = el("div", "settings__row-label");
  if (iconKey) {
    const ic = el("span", "settings__row-icon");
    ic.appendChild(Icon(iconKey, 16));
    left.appendChild(ic);
  }
  left.appendChild(el("span", undefined, label));
  row.appendChild(left);
  const right = el("div", "settings__row-value");
  if (value) right.appendChild(el("span", undefined, value));
  if (opts.chevron) {
    const ch = el("span", "settings__row-chev");
    ch.appendChild(Icon("chevronRight", 14));
    right.appendChild(ch);
  }
  row.appendChild(right);
  return row;
}

// Parse the `atmos://pair?u=<url>&c=<code>` payload that the Mac app
// QR encodes. Returns null on malformed input.
function parsePairPayload(text: string): { u: string; c: string } | null {
  try {
    const url = new URL(text);
    const u = url.searchParams.get("u") ?? "";
    const c = url.searchParams.get("c") ?? "";
    if (u && c) return { u, c };
  } catch { /* fall through */ }
  return null;
}

import { scanQR } from "../lib/qrScanner";

async function tryScan(
  urlIn: HTMLInputElement,
  codeIn: HTMLInputElement,
  saveBtn: HTMLButtonElement,
): Promise<void> {
  let text: string | null;
  try {
    text = await scanQR({ prompt: "Point at the Mac QR code" });
  } catch (err) {
    alert((err as Error).message);
    return;
  }
  if (!text) return; // user cancelled
  const parsed = parsePairPayload(text);
  if (!parsed) {
    alert("That QR isn't an atmos://pair link. Try again.");
    return;
  }
  urlIn.value = parsed.u;
  codeIn.value = parsed.c;
  localStorage.setItem("atmos.ws_url", parsed.u);
  localStorage.setItem("atmos.pair_code", parsed.c);
  saveBtn.click();
}

function buildSyncSection(): HTMLElement {
  const group = el("div", "settings__group");
  const mk = (lbl: string, key: string, ph: string, max?: number, upper?: boolean) => {
    const row = el("div", "settings__row"), left = el("div", "settings__row-label");
    left.appendChild(el("span", undefined, lbl));
    const inp = el("input", "settings__input");
    inp.type = "text"; inp.placeholder = ph; if (max) inp.maxLength = max;
    inp.value = localStorage.getItem(key) ?? "";
    inp.addEventListener("input", () => {
      if (upper) inp.value = inp.value.toUpperCase();
      localStorage.setItem(key, inp.value.trim()); });
    row.append(left, inp); group.appendChild(row); return inp;
  };
  const urlIn = mk("Mac bridge URL", "atmos.ws_url", "wss://*.trycloudflare.com");
  const codeIn = mk("Pair code", "atmos.pair_code", "ABC123", 6, true);
  const actionRow = el("div", "settings__row");
  const scanBtn = el("button", "settings__chip", "Scan QR");
  scanBtn.type = "button";
  const saveBtn = el("button", "settings__chip settings__chip--active", "Save & connect");
  saveBtn.type = "button";
  const status = el("span", "settings__status", "Not connected");
  const update = (): void => { const s = atmosStore.linkState();
    status.textContent = s === "connecting" ? "Connecting…"
      : s === "paired" ? `Paired ✓ (${atmosStore.convs().length} agents)`
      : s === "error" ? "Error" : "Not connected"; };
  atmosStore.onLink(update); atmosStore.onConvs(update); update();
  saveBtn.addEventListener("click", () => {
    const u = urlIn.value.trim(), c = codeIn.value.trim().toUpperCase();
    localStorage.setItem("atmos.ws_url", u);
    localStorage.setItem("atmos.pair_code", c);
    if (u && c) atmosWS.connect(u, c); });
  scanBtn.addEventListener("click", () => tryScan(urlIn, codeIn, saveBtn));
  actionRow.append(scanBtn, saveBtn, status); group.appendChild(actionRow); return group;
}

export function Settings(onClose: () => void): HTMLElement {
  const root = el("aside", "settings");
  root.setAttribute("aria-label", "Settings");

  // Header
  const header = el("header", "settings__header");
  header.appendChild(el("h2", "settings__title", "Settings"));
  const closeBtn = el("button", "settings__close");
  closeBtn.type = "button";
  closeBtn.setAttribute("aria-label", "Close settings");
  closeBtn.appendChild(Icon("close", 18));
  closeBtn.addEventListener("click", onClose);
  header.appendChild(closeBtn);
  root.appendChild(header);

  // Profile
  const profile = el("button", "settings__profile");
  profile.type = "button";
  const av = el("div", "settings__profile-avatar");
  av.appendChild(Avatar({ name: "Tariqul Islam", sizePx: 48 }));
  profile.appendChild(av);
  const pText = el("div", "settings__profile-text");
  pText.appendChild(el("div", "settings__profile-name", "Tariqul Islam"));
  pText.appendChild(el("div", "settings__profile-sub", "Edit profile"));
  profile.appendChild(pText);
  const editIcon = el("span", "settings__profile-edit");
  editIcon.appendChild(Icon("noteEdit", 16));
  profile.appendChild(editIcon);
  root.appendChild(profile);

  // Appearance
  root.appendChild(el("div", "settings__section-label", "Appearance"));
  const appearance = el("div", "settings__group");
  appearance.appendChild(buildThemeRow(detectTheme()));
  appearance.appendChild(
    buildStaticRow("translate", "Language", "English", { chevron: true }),
  );
  root.appendChild(appearance);

  root.appendChild(el("div", "settings__section-label", "Mac sync"));
  root.appendChild(buildSyncSection());

  // Account
  root.appendChild(el("div", "settings__section-label", "Account"));
  const account = el("div", "settings__group");
  account.appendChild(buildStaticRow(null, "Connected as", "@atmosphere_ai"));
  account.appendChild(
    buildStaticRow("logout", "Sign out", "", { destructive: true }),
  );
  root.appendChild(account);

  return root;
}

export default Settings;
